import { HeartFillIcon, HeartIcon } from '@svgs'
import { Menu, MenuItem } from '@mui/material'
import clsx from 'clsx'
import { FC, MouseEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  useGetPropertySearchesQuery,
  useGetUserQuery,
  useMakeFavoriteMutation,
} from 'src/redux/api'
import { useAppDispatch } from 'src/redux/hooks'
import { pulseFavorite } from 'src/redux/slices/uiSlice'

interface IFavoriteHeartButton {
  propertyId: string
  isFavorite?: boolean
  // Hidden on the viewer's own listing — hearting yourself makes no sense
  ownerId?: string
  // Favorites live on (property, запит) rows, so a user without a single
  // запит cannot save yet — the parent opens CreateRequestModal here
  onNeedRequest?: () => void
  className?: string
}

// Heart for the primary browse surfaces — map cards and the announcement
// page (39.8.1). One запит: saved silently; several: a small picker; none:
// nudge to create one. Removal clears the heart across all запити on the BE.
const FavoriteHeartButton: FC<IFavoriteHeartButton> = ({
  propertyId,
  isFavorite,
  ownerId,
  onNeedRequest,
  className,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { data: user } = useGetUserQuery(undefined)
  const { data: searches } = useGetPropertySearchesQuery(undefined, { skip: !user })
  const [makeFavorite] = useMakeFavoriteMutation()
  const dispatch = useAppDispatch()
  const [saved, setSaved] = useState(!!isFavorite)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)

  useEffect(() => setSaved(!!isFavorite), [isFavorite])

  if (user && ownerId && user.id === ownerId) return null

  // Success feedback comes from the global toast middleware showing the
  // (localized) backend message — a local toast here would double it
  const toggle = async (propertySearchId: string) => {
    const next = !saved
    setSaved(next)
    try {
      await makeFavorite({ propertyId, propertySearchId, isFavorite: next }).unwrap()
      // Let the sidebar "Вибране" item feel the add too (owner feedback).
      if (next) dispatch(pulseFavorite())
    } catch {
      setSaved(!next)
    }
  }

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (!user) {
      // Same contract as the auth guards (19.2): LoginForm returns to `from`
      navigate('/login', { state: { from: location.pathname + location.search } })
      return
    }
    const list = searches || []
    if (list.length === 0) {
      toast.info(t('favorite.need_request'))
      onNeedRequest?.()
      return
    }
    // Removal is global on the backend, so any owned запит id works
    if (saved || list.length === 1) {
      toggle(list[0].id)
      return
    }
    setMenuAnchor(e.currentTarget)
  }

  return (
    <>
      <button
        onClick={handleClick}
        aria-label={t('favorite.button')}
        className={clsx(
          'flex items-center justify-center w-11 h-11 md:w-9 md:h-9 rounded-full shadow pointer-events-auto transition-colors duration-200',
          saved ? 'bg-danger-strong' : 'bg-surface hover:bg-brand-soft',
          className,
        )}
      >
        <span className={clsx('icon-button w-4 h-4', saved ? 'fill-white' : 'fill-danger-strong')}>
          {saved ? <HeartFillIcon /> : <HeartIcon />}
        </span>
      </button>
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem disabled className="!text-xs !opacity-70">
          {t('favorite.pick_request')}
        </MenuItem>
        {(searches || []).map((search: { id: string; label: string }) => (
          <MenuItem
            key={search.id}
            className="!min-h-11"
            onClick={() => {
              setMenuAnchor(null)
              toggle(search.id)
            }}
          >
            {search.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default FavoriteHeartButton
